import { formatDate } from '@angular/common';
import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { filter, firstValueFrom, interval, map, Observable, pairwise, Subject, takeUntil } from 'rxjs';
import { AttendanceEntry, TId } from 'src/app/models';
import { MasterApiService } from 'src/app/services/master-api.service';
import { FormBuilder, FormGroup } from '@angular/forms';
import { CellClickEvent, CellPreparedEvent, ToolbarItem } from 'devextreme/ui/data_grid';
import { getEndOfWeek, getStartOfWeek, getTodayUTCDate, getWeekNumber, secondsToHhmmss, secondsToTimeSpanString } from 'src/app/libs/utils';
import { LangService } from 'src/app/services/lang.service';
import { AttendanceEntriesAddComponent } from '../attendance-entries-add/attendance-entries-add.component';
import { GlamResourceDirective } from 'src/app/libs/directives/glam-resource.directive';
import { DxDataGridComponent } from 'devextreme-angular';
import { WSClient } from 'src/app/libs/WSClient';
import { AlertService } from 'src/app/services/alert.service';
import { GenericResource } from 'src/app/resources';
import { AuthService } from 'src/app/services/auth.service';
import { TranslateService } from '@ngx-translate/core';
import { icons } from 'src/icons';
import { ucfirst } from 'src/app/libs/utils';

interface WorkerAttendanceTerminal {
  workerId: TId | undefined;
  weekNumber: number;
  startOfWeek: Date;
  endOfWeek: Date;
}

@Component({
  selector: 'app-clocking-in-terminal',
  templateUrl: './clocking-in-terminal.component.html',
  styleUrls: ['./clocking-in-terminal.component.sass'],
  standalone: false,
})
export class ClockingInTerminalComponent implements OnInit, AfterViewInit, OnDestroy {

  destroy$: Subject<void>;
  entity?: WorkerAttendanceTerminal;
  form: FormGroup | undefined;
  selectedEntryForEdit: AttendanceEntry | null = null;
  editPopupVisible = false;
  secondsToHhmmss = secondsToHhmmss;
  secondsToStringTime = secondsToTimeSpanString;
  langCode$ = this.lang.code$;
  today = new Date();
  todayEnd = new Date();
  todayWeek: number = getWeekNumber(this.today);

  entriesAddComponent = AttendanceEntriesAddComponent;

  clockingResource?: GenericResource<AttendanceEntry>;
  weekNavigatorResource?: GenericResource<AttendanceEntry>;
  incidencesResource?: GenericResource<AttendanceEntry>;

  @ViewChild('glamResourceDir', { static: false }) weekResourceDirective!: GlamResourceDirective<AttendanceEntry>;
  @ViewChild('todayEntriesGrid', { static: false }) todayEntriesGrid: DxDataGridComponent | undefined;
  @ViewChild('weekEntriesGrid', { static: false }) weekEntriesGrid: DxDataGridComponent | undefined;
  @ViewChild('incidencesGrid', { static: false }) incidencesGrid: DxDataGridComponent | undefined;

  weekNavToolbar: ToolbarItem[] = [];

  /** Updated in onCellPrepared; used to refresh the live total without repainting the grid (repaint breaks row templates / async). */
  private todayAttendanceFooterCellEl: HTMLElement | null = null;

  constructor(private api: MasterApiService, private fb: FormBuilder, private lang: LangService, private alertSvc: AlertService, private authService: AuthService, private translationService: TranslateService) {
    this.destroy$ = new Subject();

    this.authService.user$.pipe(
      takeUntil(this.destroy$),
      pairwise(),
      filter(([prev, cur]) => prev?.lastCompanyId !== cur?.lastCompanyId),
    ).subscribe(async () => {
      await this.preFetch();
      this.refreshUpdatedEntity(undefined);
    });
  }

  async ngOnInit(): Promise<void> {
    await this.preFetch();

    this.form = this.fb.group({
      workerId: [this.entity?.workerId],
      weekNumber: [this.entity?.weekNumber],
      startOfWeek: [this.entity?.startOfWeek],
      endOfWeek: [this.entity?.endOfWeek]
    });

    this.weekNavToolbar = [
    {
      name: 'weekRangeLabel',
      location: 'after',
      cssClass: 'week-toolbar-week-range',
      widget: 'dxButton',
      options: {
        text: '',
        stylingMode: 'text',
        hoverStateEnabled: false,
        focusStateEnabled: false,
        activeStateEnabled: false,
        tabIndex: -1,
        elementAttr: {
          class: 'dx-theme-accent-as-text-color',
        },
      },
    },
    {
      name: 'weekPrev',
      options: {
        icon: icons['chevron_left'],
        hint: ucfirst(this.translationService.instant('common.moveToPreviousWeek')),
        onClick: () => {this.navigateWeek(-1)},
      },
    },
    {
      name: 'weekNext',
      location: 'after',
      widget: 'dxButton',
      options: {
        icon: icons['chevron_right'],
        hint: ucfirst(this.translationService.instant('common.moveToFolowingWeek')),
        onClick: () => this.navigateWeek(1),
      },
    },
    {
      name: 'weekToday',
      options: {
        icon: icons['today'],
        hint: ucfirst(this.translationService.instant('common.goToCurrentWeek')),
        onClick: () => this.navigateWeek(0),
      },
    },
  ];
    this.refreshWeekRangeToolbarLabel();
  }

  ngAfterViewInit(): void {
    interval(1000).pipe(takeUntil(this.destroy$)).subscribe(() => {
      const el = this.todayAttendanceFooterCellEl;
      if (el?.isConnected) {
        el.textContent = this.formatTodayAttendanceFooter({});
      }
    });
  }

  onTodayEntriesCellPrepared(e: CellPreparedEvent<AttendanceEntry, unknown>): void {
    if (e.rowType !== 'totalFooter' || e.column?.dataField !== 'attendanceTime') {
      return;
    }
    const text = this.formatTodayAttendanceFooter({});
    const target = this.summaryFooterTextTarget(e.cellElement);
    target.textContent = text;
    this.todayAttendanceFooterCellEl = target;
  }

  /** DevExtreme wraps footer text in inner nodes; writing to the <td> strips them and changes look vs other grids. */
  private summaryFooterTextTarget(cell: HTMLElement): HTMLElement {
    return (
      cell.querySelector<HTMLElement>('.dx-datagrid-text-content') ??
      cell.querySelector<HTMLElement>('.dx-datagrid-summary-item') ??
      cell
    );
  }

  ngOnDestroy(): void {
    this.todayAttendanceFooterCellEl = null;
    this.destroy$.next();
    this.destroy$.complete();
  }

  async preFetch(): Promise<void> {

    this.today = getTodayUTCDate();
    this.today.setUTCHours(0, 0, 0, 0);
    this.todayWeek = getWeekNumber(this.today);
    this.todayEnd = new Date(Date.UTC(this.today.getUTCFullYear(), this.today.getUTCMonth(), this.today.getUTCDate() + 1));

    const startOfWeek = getStartOfWeek(this.today);
    const endOfWeek = getEndOfWeek(this.today);

    this.entity = {
      workerId: this.api.auth.userValue?.workerId,
      weekNumber: getWeekNumber(this.today),
      startOfWeek: startOfWeek,
      endOfWeek: endOfWeek,
    };

    if (this.entity.workerId) {
      this.clockingResource = this.api.resources.attendanceEntriesMe.contextualizeQueryParams({ startDate: this.today.toISOString(), endDate: this.todayEnd.toISOString(), o: 'entryIn,asc' })
        .asMappedResource({
          from: async (data) => {
            data.day = data.entryIn;
            if (data.entryOut === null || data.entryOut === undefined) {
              data.openedAttendanceTime = this.getOpenedAttendanceTime$(data.entryIn);
            }else {
              data.openedAttendanceTime = data.attendanceTime;
            }
            return data;
          }
        }) as GenericResource<AttendanceEntry>;
      this.weekNavigatorResource = this.api.resources.attendanceEntriesMe.contextualizeQueryParams({ startDate: startOfWeek.toISOString(), endDate: endOfWeek > this.today ? this.today.toISOString() : endOfWeek.toISOString(), o: 'entryIn,asc' })
      .asMappedResource({
        from: async (data) => {
          data.day = data.entryIn;
          return data;
        }
      }) as GenericResource<AttendanceEntry>;
      this.incidencesResource = this.api.resources.attendanceEntriesMe.contextualizeQueryParams({ incidence: 'true', o: 'entryIn,asc' })
      .asMappedResource({
        from: async (data) => { 
          data.day = data.entryIn;
          return data;
        }
      }) as GenericResource<AttendanceEntry>;
    }

    await this.subscribeWS();
  }

  getOpenedAttendanceTime$(date: Date): Observable<number> {
    return interval(1000).pipe(
      map(() => {
        const now = new Date();
        const diferenceMS = Math.abs(now.getTime() - new Date(date).getTime());

        return diferenceMS / 1000;
      })
    );
  }

  private async subscribeWS() {
    if (this.api.auth.userValue?.workerId) {
      const target = `AttendanceEntriesIncidencesCalculated/${this.api.auth.userValue!.workerId}`;
      const wsClient = new WSClient('/hubs/resources');
      try {
        await wsClient.connect();
        this.destroy$.subscribe(() => wsClient.disconnect());
        wsClient.listen(target).pipe(
          takeUntil(this.destroy$)
        ).subscribe(async () => {
          await this.refreshUpdatedEntity(undefined);
        });
      } catch (e) {
        this.alertSvc.error('Error subscribing to DocumentPendingBatchesUpdated');
      }
    }
  }

  async createEntryOut() {
    if (!this.entity || !this.entity.workerId || !this.clockingResource) return;

    let dateNow: Date = new Date();
    dateNow = new Date(Date.UTC(dateNow.getUTCFullYear(), dateNow.getUTCMonth(), dateNow.getUTCDate(), dateNow.getUTCHours(), dateNow.getUTCMinutes(), dateNow.getUTCSeconds()));

    const updatedEntity = await firstValueFrom(this.clockingResource.action('clockOut', 
      {
        date: dateNow,
        companyId: this.api.auth.userValue!.lastCompanyId
      }));
    // Update the local state with the updated entry
    this.refreshUpdatedEntity(updatedEntity);
  }

  async createEntryIn() {
    if (!this.entity || !this.entity.workerId || !this.clockingResource) return;

    let dateNow: Date = new Date();
    dateNow = new Date(Date.UTC(dateNow.getUTCFullYear(), dateNow.getUTCMonth(), dateNow.getUTCDate(), dateNow.getUTCHours(), dateNow.getUTCMinutes(), dateNow.getUTCSeconds()));

    const updatedEntity = await firstValueFrom(this.clockingResource.action('clockIn', {
      date: dateNow,
      companyId: this.api.auth.userValue!.lastCompanyId
    }));
    // Update the local state with the updated entry
    this.refreshUpdatedEntity(updatedEntity);
  }

  private refreshUpdatedEntity(updatedEntity: AttendanceEntry | undefined) {
    this.todayEntriesGrid?.instance.refresh();
    this.weekEntriesGrid?.instance.refresh();
    this.incidencesGrid?.instance.refresh();
  }

  onCellClick(e: CellClickEvent<AttendanceEntry>): void {
    const dataField = e.column?.dataField;
    if (!this.entity?.workerId) return;
    if (dataField !== 'entryIn' && dataField !== 'entryOut' && dataField !== 'day') return;
    const row = e.data as AttendanceEntry | undefined;
    if (!row?.id) return;
    this.selectedEntryForEdit = row;
    this.editPopupVisible = true;
  }

  onEditPopupHidden(): void {
    this.editPopupVisible = false;
    this.selectedEntryForEdit = null;
  }

  async updateEditedEntry(updatedEntity: AttendanceEntry): Promise<void> {
    this.refreshUpdatedEntity(updatedEntity);
    this.editPopupVisible = false;
    this.selectedEntryForEdit = null;
  }

  navigateWeek(weekNavigation: number) {
    if (this.weekNavigatorResource) {
      // Get current startOfWeek
      const newStartOfWeek = weekNavigation === 0 ? getStartOfWeek(this.today) : new Date(Date.UTC(this.entity!.startOfWeek.getUTCFullYear(), this.entity!.startOfWeek.getUTCMonth(), this.entity!.startOfWeek.getUTCDate() + (weekNavigation * 7)));
      const newEndOfWeek = getEndOfWeek(newStartOfWeek);
      const weekNumber = getWeekNumber(newStartOfWeek);

      // Update the form values
      this.form?.patchValue({
        startOfWeek: newStartOfWeek,
        endOfWeek: newEndOfWeek,
        weekNumber: weekNumber
      });

      // Update the entity values
      this.entity!.startOfWeek = newStartOfWeek;
      this.entity!.endOfWeek = newEndOfWeek;
      this.entity!.weekNumber = weekNumber;

      // Fetch the new week entries
      this.weekNavigatorResource = this.weekNavigatorResource.contextualizeQueryParams(
        {
          startDate: newStartOfWeek.toISOString(),
          endDate: newEndOfWeek > this.today ? this.today.toISOString() : newEndOfWeek.toISOString(),
          o: 'entryIn,asc'
        });

      this.weekResourceDirective?.changeResource(this.weekNavigatorResource);
      this.refreshWeekRangeToolbarLabel();
    }
  }

  private refreshWeekRangeToolbarLabel(): void {
    const hintOpts = (name: string, key: string) => {
      const toolbarItem = this.weekNavToolbar.find(i => i.name === name);
      if (toolbarItem?.options && typeof toolbarItem.options === 'object') {
        (toolbarItem.options as { hint?: string }).hint = ucfirst(this.translationService.instant(key));
      }
    };
    hintOpts('weekPrev', 'common.moveToPreviousWeek');
    hintOpts('weekNext', 'common.moveToFolowingWeek');
    hintOpts('weekToday', 'common.goToCurrentWeek');

    const labelItem = this.weekNavToolbar.find(i => i.name === 'weekRangeLabel');
    if (labelItem?.widget === 'dxButton' && labelItem.options && this.entity) {
      const weekLabel = this.translationService.instant('common.week');
      const loc = this.lang.code;
      const start = formatDate(this.entity.startOfWeek, 'dd/MM/yyyy', loc);
      const end = formatDate(this.entity.endOfWeek, 'dd/MM/yyyy', loc);
      (labelItem.options as { text?: string }).text =
        `${weekLabel} ${this.entity.weekNumber} | ${start} - ${end}`;
    }
    this.weekEntriesGrid?.instance.repaint();
  }

  formatSummaryAttendanceTime = (data: any) => {
    let value = data;
    if('value' in data){
      value = data.value
    }
    // return this.translationService.instant('entity.attendanceEntry.attendanceTime') + ': ' + this.secondsToStringTime(value);
    return this.secondsToStringTime(value);
  }

  /** Footer total for “today” grid: sums attendanceTime for closed rows and live elapsed seconds for open rows (same as cell template). */
  formatTodayAttendanceFooter = (_data: { value?: number }): string => {
    // return this.translationService.instant('entity.attendanceEntry.attendanceTime') + ': ' + this.secondsToStringTime(this.getTodayAttendanceTotalSeconds());
    return this.secondsToStringTime(this.getTodayAttendanceTotalSeconds());
  };

  private getTodayAttendanceTotalSeconds(): number {
    const items = this.todayEntriesGrid?.instance.getDataSource()?.items() as AttendanceEntry[] | undefined;
    if (!items?.length) {
      return 0;
    }
    let total = 0;
    for (const row of items) {
      total +=
        row.entryOut === null || row.entryOut === undefined
          ? Math.abs(Date.now() - new Date(row.entryIn).getTime()) / 1000
          : (row.attendanceTime ?? 0);
    }
    return total;
  }
}
